import * as React from "react";

import { appendToClassName, estimateStringLengthInPixels } from "lib/helpers";

function getValidationIcon(valid: boolean, className?: string): JSX.Element {
  let iconClass = "fa fa-check";
  if (!valid) {
    iconClass = "fa fa-exclamation-triangle";
  }
  return (
    <i
      className={`form-feedback-icon ${iconClass} ${className}`}
      aria-hidden="true"
      style={{ marginLeft: "10px" }}
    />
  );
}

export function FormRow(props: {
  children?: React.ReactNode;
  bottom?: React.ReactNode;
  className?: string;
}) {
  const { className, bottom, children } = props;

  const style: React.CSSProperties = {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "end",
  };

  return (
    <>
      <div className={`form-row-container ${className || ""}`}>
        <div style={style} className={`seller-form-row ${className || ""}`}>
          {children}
        </div>
        {bottom}
      </div>
    </>
  );
}

export type Option = {
  readonly value: string;
  readonly display: string;
};

interface FormInputProps {
  id?: string;
  title: string;
  placeholder?: string;
  onChange: (value: string) => void;
  type?: string;
  options?: Option[];
  required: boolean;
  flexBasis?: string;
  value: string;
  maxLength?: number;
  validator: (value: string) => boolean;
  callbackIfValid?: (value: string) => void;
  autoComplete: string;
  onBlur?: (value: string) => void;
  hideValidationIcon?: boolean;
  disabled?: boolean;
  selectPlaceholder?: JSX.Element;
  suffixIfNotEmpty?: string;
  prefixIfNotEmpty?: string;
  prefix?: string;
  className?: string;
  autoFocus?: boolean;
}

interface FormInputState {
  blurred: boolean;
}

export class FormInput extends React.Component<FormInputProps, FormInputState> {
  constructor(props: FormInputProps) {
    super(props);
    this.state = {
      blurred: false,
    };
  }

  public render() {
    const {
      flexBasis,
      id,
      type,
      title,
      placeholder,
      onChange,
      required,
      value,
      maxLength,
      validator,
      autoComplete,
      onBlur,
      callbackIfValid,
      hideValidationIcon,
      disabled,
      options,
      selectPlaceholder,
      prefixIfNotEmpty,
      suffixIfNotEmpty,
      prefix,
      className,
      autoFocus,
    } = this.props;

    const style: React.CSSProperties = {
      flexBasis: flexBasis || "100%",
      textAlign: "left",
    };

    const valid = validator(value);
    let borderColor = "";
    if (value && value.length > 0) {
      if (!valid) {
        borderColor = "red";
      } else if (callbackIfValid) {
        callbackIfValid(value);
      }
    }
    const inputStyle: React.CSSProperties = {
      border: "none",
      background: "white",
      outline: "none",
      borderRadius: 0,
      color:
        value && value.length > 0
          ? "rgba(42, 49, 59, 1)"
          : "rgba(42, 49, 59, .5)",
      width:
        value && suffixIfNotEmpty
          ? estimateStringLengthInPixels(value) * 1.8 + 5
          : undefined,
    };

    let component;

    switch (type) {
      case "select":
        component = (
          <div
            className={appendToClassName("input-field", className)}
            style={{
              position: "relative",
            }}
          >
            <select
              id={id}
              style={inputStyle}
              value={value || ""}
              onChange={e => onChange(e.currentTarget.value)}
              disabled={disabled}
              required={required}
            >
              <>
                {selectPlaceholder}
                {options &&
                  options.map((option, i) => {
                    return (
                      <option key={i} value={option.value}>
                        {option.display}
                      </option>
                    );
                  })}
              </>
            </select>
            <div
              style={{
                position: "absolute",
                top: "30%",
                right: 0,
                fontSize: "24px",
                color: "black",
                pointerEvents: "none",
                paddingLeft: "5px",
                background: "white",
              }}
            >
              <i className="fa fa-angle-down" aria-hidden="true" />
            </div>
          </div>
        );
        break;
      case "radio-select":
        component = (
          <div className={appendToClassName("radio-select", className)}>
            {options &&
              options.map((option, i) => {
                return (
                  <div
                    key={i}
                    className="radio-select-item"
                    onClick={() => onChange(option.value)}
                  >
                    <input
                      key={i}
                      type="radio"
                      name={id}
                      value={option.value}
                      checked={value === option.value}
                      onChange={() => undefined}
                    />
                    {option.display}
                  </div>
                );
              })}
          </div>
        );
        break;
      default:
        component = (
          <div
            className={appendToClassName(
              "input-field input-field-container",
              className,
            )}
            style={{
              borderBottomColor: borderColor,
            }}
          >
            {value && prefixIfNotEmpty && (
              <label htmlFor={id} className="input-prefix">
                {prefixIfNotEmpty}
              </label>
            )}
            {prefix && (
              <label htmlFor={id} className="input-prefix">
                {prefix}
              </label>
            )}
            <input
              id={id}
              type={type || "text"}
              style={inputStyle}
              placeholder={placeholder}
              onChange={e => onChange(e.target.value)}
              required={required}
              value={value}
              maxLength={maxLength}
              autoComplete={autoComplete}
              onBlur={e => onBlur && onBlur(e.target.value)}
              disabled={disabled}
              autoFocus={autoFocus}
            />
            {value && suffixIfNotEmpty && (
              <label className="sufix-prefix">{suffixIfNotEmpty}</label>
            )}
          </div>
        );
    }

    return (
      <div style={style}>
        <label htmlFor={id}>
          {title}
          {!hideValidationIcon &&
            value &&
            value.length > 0 &&
            getValidationIcon(valid)}
        </label>
        {component}
      </div>
    );
  }
}
